import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../firebase/firebaseConfig';

// Admin draft replies.
//
// Drafts live at supportConversations/{accountUUID}/adminDrafts/{adminUid} —
// deliberately NOT on the conversation document, which the customer is allowed
// to read (see firestore.rules). Subcollection rules do not cascade from the
// parent match, so this path is admin-only. One document per admin, keyed by
// auth uid, so two admins drafting to the same customer never clobber each
// other.
//
// There is no "save" button: the draft is written on a debounce while typing
// and force-flushed on the exits where a pending timer would otherwise lose the
// tail of the text (unmount, conversation switch, page unload).

// Accepted limit of the design: a reload or tab close within DEBOUNCE_MS of a
// keystroke can lose that last second of typing. `visibilitychange` and
// `pagehide` cover hiding, minimising and backgrounding, but a straight reload
// tears the document down before the write can leave, and the Firestore web SDK
// offers no keepalive path (offline persistence is not enabled here). The
// status line reads "Saving…" throughout that window, so the UI never claims a
// persistence it does not have. Weighed and accepted rather than overlooked.
const DEBOUNCE_MS = 1000;
const RETRY_DELAY_MS = 3000;
const LOAD_ATTEMPTS = 3;
const LOAD_RETRY_DELAY_MS = 2000;

function draftRef(accountUUID, uid) {
  return doc(db, 'supportConversations', accountUUID, 'adminDrafts', uid);
}

// Write-or-delete. Content that trims to empty deletes the document, so an
// emptied composer does not leave a phantom draft indicator behind. Deleting a
// document that does not exist is a no-op in Firestore.
async function writeDraft(accountUUID, text) {
  const uid = auth.currentUser?.uid;
  // Throw rather than resolve: a silent no-op here would be indistinguishable
  // from a successful write, and the hook would claim a persistence it never
  // achieved. In practice the dashboard is gated on a signed-in admin.
  if (!uid) throw new Error('Cannot save draft: no signed-in admin');
  if (!accountUUID) throw new Error('Cannot save draft: no account');

  const ref = draftRef(accountUUID, uid);
  if (text.trim()) {
    await setDoc(ref, {
      content: text,
      accountUUID,
      updatedAt: serverTimestamp(),
      updatedByEmail: auth.currentUser?.email || null
    });
  } else {
    await deleteDoc(ref);
  }
}

export function useMessageDraft(accountUUID) {
  const [draft, setDraftState] = useState('');
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [loadError, setLoadError] = useState(false);
  // Tracked rather than read once: on a cold load the composer can mount before
  // Firebase has restored the session, and the load must not run without a uid.
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);

  const pendingRef = useRef('');   // latest text in the composer
  const savedRef = useRef('');     // text we believe is persisted
  const loadedRef = useRef(false); // mirrors draftLoaded for use inside callbacks
  const accountRef = useRef(accountUUID);
  const saveTimerRef = useRef(null);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => setUid(user?.uid || null));
  }, []);

  // Describe reality when there is nothing to write: blank when no draft is
  // stored, "Draft saved HH:MM" when one is. Without this, editing back to the
  // persisted text would strand the line on "Saving…" forever.
  const settleStatus = useCallback((text) => {
    if (!mountedRef.current || pendingRef.current !== text) return;
    if (text.trim()) {
      setSaveState('saved');
    } else {
      setSaveState('idle');
      setLastSavedAt(null);
    }
  }, []);

  // Perform a save. Skips when nothing changed, so opening a conversation and
  // closing it without typing never writes.
  const runSave = useCallback((account, text, isRetry = false) => {
    if (!account) return;
    if (text === savedRef.current) {
      settleStatus(text);
      return;
    }

    setSaveState('saving');
    writeDraft(account, text)
      .then(() => {
        // A write that lands after a conversation switch must not touch the
        // hook's state at all: savedRef now describes a different conversation,
        // and stamping this text into it would make a later real save look
        // redundant and be skipped.
        if (accountRef.current !== account) return;
        savedRef.current = text;
        if (!mountedRef.current) return;
        // A later keystroke may already have superseded this write; only report
        // on the state that is actually on screen now.
        if (pendingRef.current !== text) return;
        if (text.trim()) {
          setSaveState('saved');
          setLastSavedAt(new Date());
        } else {
          // This write was a delete — there is no stored draft to report.
          setSaveState('idle');
          setLastSavedAt(null);
        }
      })
      .catch((err) => {
        console.error('Error saving draft:', err);
        if (!mountedRef.current || accountRef.current !== account) return;
        setSaveState('error');
        if (isRetry) return; // one retry only; leave the text in the composer
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          runSave(account, pendingRef.current, true);
        }, RETRY_DELAY_MS);
      });
  }, [settleStatus]);

  const setDraft = useCallback((text) => {
    setDraftState(text);
    pendingRef.current = text;
    if (!loadedRef.current) return; // never save over a draft we have not read yet

    // Report the pending write immediately. Leaving the line on
    // "Draft saved 14:32" through the debounce window would assert that
    // characters typed a moment ago are persisted when they are not. Editing
    // back to the stored text settles the line instead of leaving it pending.
    if (text !== savedRef.current) {
      setSaveState('saving');
    } else {
      settleStatus(text);
    }

    clearTimeout(saveTimerRef.current);
    clearTimeout(retryTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      runSave(accountRef.current, pendingRef.current);
    }, DEBOUNCE_MS);
  }, [runSave, settleStatus]);

  // Load the stored draft once per conversation. A one-shot read, not a
  // subscription: a live listener would echo our own saves back and could
  // clobber characters typed in the interim.
  useEffect(() => {
    let cancelled = false;

    accountRef.current = accountUUID;
    loadedRef.current = false;
    setDraftLoaded(false);
    setDraftState('');
    pendingRef.current = '';
    savedRef.current = '';
    setSaveState('idle');
    setLastSavedAt(null);
    setLoadError(false);

    // No signed-in admin yet: hold the composer closed rather than presenting
    // an empty one, which would be indistinguishable from "no draft stored".
    // The effect re-runs when auth resolves, so this is self-healing.
    if (!uid) {
      return () => { cancelled = true; };
    }

    if (!accountUUID) {
      loadedRef.current = true;
      setDraftLoaded(true);
      return () => { cancelled = true; };
    }

    // A read that failed must never be reported as a read that found nothing:
    // the composer would open blank with savedRef at '', and the admin's next
    // keystroke would overwrite the stored draft the read did not retrieve
    // (writeDraft is a full setDoc, and there is no offline cache to fall back
    // on). So the composer stays closed until a read genuinely succeeds.
    (async () => {
      for (let attempt = 0; attempt < LOAD_ATTEMPTS; attempt++) {
        try {
          const snap = await getDoc(draftRef(accountUUID, uid));
          if (cancelled) return;
          const data = snap.exists() ? snap.data() : null;
          const content = data?.content || '';
          setDraftState(content);
          pendingRef.current = content;
          savedRef.current = content;
          if (content) {
            setSaveState('saved');
            setLastSavedAt(data?.updatedAt?.toDate?.() || null);
          }
          loadedRef.current = true;
          setDraftLoaded(true);
          setLoadError(false);
          return;
        } catch (err) {
          console.error(`Error loading draft (attempt ${attempt + 1}/${LOAD_ATTEMPTS}):`, err);
          if (cancelled) return;
          // A rules denial is evaluated server-side and will be identical every
          // time — retrying it only holds the composer disabled for no gain.
          // This is the state before the adminDrafts rules are deployed.
          if (err?.code === 'permission-denied') break;
          if (attempt < LOAD_ATTEMPTS - 1) {
            await new Promise(resolve => setTimeout(resolve, LOAD_RETRY_DELAY_MS));
            if (cancelled) return;
          }
        }
      }
      // Out of attempts. `draftLoaded` stays false, which keeps every write path
      // refused (setDraft, flush and clearDraft all bail on it) so the stored
      // draft we failed to read is never overwritten. Drafting is off for this
      // conversation, but the composer itself must stay usable — the most
      // likely cause of a read failure is the adminDrafts rule not being
      // deployed yet, which has no bearing on whether a message can be sent.
      if (!cancelled) setLoadError(true);
    })();

    return () => { cancelled = true; };
  }, [accountUUID, uid]);

  // Flush on the exits a debounce would otherwise lose: unmount, conversation
  // switch, and the page going away. Cleanup runs before the next effect's
  // setup, so the refs still hold the outgoing conversation's text here.
  useEffect(() => {
    const account = accountUUID;

    const flush = () => {
      if (!loadedRef.current) return;
      if (pendingRef.current === savedRef.current) return;
      const text = pendingRef.current;
      // Only claim the text is persisted once the write has actually resolved,
      // so a failed flush cannot leave the hook refusing to re-save it.
      return writeDraft(account, text).then(() => {
        if (accountRef.current === account) savedRef.current = text;
      });
    };

    const flushQuietly = () => {
      Promise.resolve(flush()).catch((err) => console.error('Error flushing draft:', err));
    };

    // `visibilitychange` → hidden and `pagehide` are the signals that actually
    // fire early enough for a network write to complete. `beforeunload` cannot
    // await a promise and the write is usually torn down with the document, so
    // it is not used here. Hiding the tab (switching tabs, closing, reloading,
    // backgrounding on mobile) therefore persists whatever is on screen.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushQuietly();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flushQuietly);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushQuietly);
      clearTimeout(saveTimerRef.current);
      clearTimeout(retryTimerRef.current);
      flushQuietly();
    };
  }, [accountUUID]);

  // Called after a message is sent successfully. A failed delete is surfaced
  // and retried like a failed save: otherwise the blue account-row dot and the
  // inbox "Draft" pill keep advertising a message that has already been sent.
  const clearDraft = useCallback(async () => {
    clearTimeout(saveTimerRef.current);
    clearTimeout(retryTimerRef.current);

    const account = accountRef.current;
    setDraftState('');
    pendingRef.current = '';

    if (savedRef.current === '') {
      setSaveState('idle');
      setLastSavedAt(null);
      return;
    }

    setSaveState('saving');
    try {
      await writeDraft(account, '');
      if (accountRef.current !== account) return;
      savedRef.current = '';
      if (!mountedRef.current) return;
      setSaveState('idle');
      setLastSavedAt(null);
    } catch (err) {
      console.error('Error clearing draft:', err);
      if (!mountedRef.current || accountRef.current !== account) return;
      setSaveState('error');
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        // pendingRef is '' unless the admin has started a new message, in which
        // case saving that supersedes the delete — either way it is correct.
        runSave(account, pendingRef.current, true);
      }, RETRY_DELAY_MS);
    }
  }, [runSave]);

  // Put text back after a failed send — but never over something newly typed.
  const restoreDraft = useCallback((text) => {
    if (pendingRef.current.trim()) return;
    setDraft(text);
  }, [setDraft]);

  return { draft, setDraft, clearDraft, restoreDraft, saveState, lastSavedAt, draftLoaded, loadError };
}
